<template>
  <div :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <JssRichText class="accordion-title" :field="fields?.title" tag="h2" />
      <JssRichText class="accordion-desc" :field="fields?.subtitle" />
      <div
        class="accordion-wrapper"
        :style="{ '--accordion-item-width': itemWidth }"
        v-if="fields?.list?.length > 0"
      >
        <SiteLink
          v-for="(item, index) in fields?.list"
          :key="index"
          class="accordion-wrapper-item"
          :class="{ 'is-active': activeIndex === index }"
          @mouseenter="handleCardEnter(index)"
          :link="item?.fields?.button?.fields?.link"
        >
          <div class="accordion-wrapper-item-bg">
            <AdaptiveImage
              class="accordion-wrapper-item-bg-before"
              is-background
              :field="item.fields?.beforeImage"
            />
            <AdaptiveImage
              class="accordion-wrapper-item-bg-after"
              is-background
              :field="item.fields?.afterImage"
            />
          </div>
          <Transition v-if="isPC" name="accordion-fade">
            <JssRichText
              class="accordion-wrapper-item-title-vertical"
              :field="item?.fields?.title"
              :tag="getTitleTag(item?.fields?.title)"
              v-if="activeIndex !== index"
            />
          </Transition>
          <div
            class="accordion-wrapper-item-content"
            :style="{
              justifyContent: fields?.layout?.fields?.phrase?.value
                ? fields.layout.fields.phrase.value
                : 'flex-end',
            }"
          >
            <JssRichText
              v-if="!isPC"
              class="accordion-wrapper-item-content-title"
              :field="item?.fields?.title"
              :tag="getTitleTag(item?.fields?.title)"
            />
            <Transition v-else name="accordion-fade">
              <JssRichText
                class="accordion-wrapper-item-content-title"
                :field="item?.fields?.title"
                :tag="getTitleTag(item?.fields?.title)"
                v-if="activeIndex === index"
              />
            </Transition>
            <div>
              <template v-if="!isPC">
                <JssRichText
                  class="accordion-wrapper-item-content-desc"
                  :field="item?.fields?.description"
                />
              </template>
              <Transition v-else name="accordion-fade">
                <JssRichText
                  class="accordion-wrapper-item-content-desc"
                  :field="item?.fields?.description"
                  v-if="activeIndex === index"
                />
              </Transition>
              <siteButton
                v-if="!isPC"
                v-bind="item.fields?.button"
                class="accordion-wrapper-item-content-btn"
              />
              <Transition v-else name="accordion-fade">
                <span
                  v-if="activeIndex === index"
                  class="accordion-wrapper-item-content-btn-wrapper"
                >
                  <siteButton
                    v-bind="item.fields?.button"
                    class="accordion-wrapper-item-content-btn"
                  />
                </span>
              </Transition>
            </div>
          </div>
        </SiteLink>
      </div>
    </ContentGrid>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import utils from '@/utils';
import useDevice from '@/hooks/useDevice';

export default {
  name: 'Accordion',
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    rendering: {
      type: Object,
    },
  },
  setup(props) {
    const activeIndex = ref(0);

    const { deviceComputes } = useDevice();
    const isPC = computed(() => deviceComputes.largeThanTablet.value);

    const itemWidth = computed(() => {
      const len = props.fields?.list?.length || 0;
      if (!len) return '320px';
      return `calc((min(1440px, 100vw / 24 * 20) - ${(len - 1) * 8}px) / ${len + 1.5} * 2.5 - 64px)`;
    });

    const cardList = computed(() => {
      const items = props.fields?.items;
      if (items && Array.isArray(items) && items.length > 0) {
        return items.map((item) => ({
          title: item.fields?.title?.value || '',
          desc: item.fields?.description?.value || '',
          image: item.fields?.image?.value?.src || '',
        }));
      }
      return [];
    });

    const methods = {
      getTitleTag(field) {
        const value = field?.value;
        return typeof value === 'string' && /<h[1-6][\s>]/i.test(value) ? 'div' : 'h3';
      },
      handleCardEnter(index) {
        activeIndex.value = index;
      },
    };

    const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'accordion'));

    const rootStyle = computed(() => {
      const result = utils.overall.baseComStyle(props.fields);
      return result;
    });

    return {
      activeIndex,
      cardList,
      isPC,
      itemWidth,
      ...methods,
      rootClass,
      rootStyle,
      utils,
    };
  },
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.accordion {
  position: relative;
  width: 100%;
  padding: 40px 0;
  word-wrap: break-word;

  &-title {
    text-align: center;
  }

  &-desc {
    font-size: 14px;
    line-height: 1.6;
    color: $black;
    margin-top: 16px;
    text-align: center;
  }

  &-wrapper {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-top: 24px;

    &-item {
      position: relative;
      border-radius: 12px;
      overflow: hidden;
      cursor: pointer;
      height: 240px;
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      border-radius: 14px;
      height: 280px;
      color: $white;

      .accordion-wrapper-item-content {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        position: absolute;
        z-index: 2;
      }

      &-bg {
        position: absolute;
        inset: 0;
        z-index: 0;
        overflow: hidden;
        width: 100%;
        height: 100%;

        &-before {
          display: none;
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-position: center center;
          }
        }

        &-after {
          display: block;
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-position: center center;
          }
        }
      }

      &-content {
        position: absolute;
        inset: 0;
        z-index: 2;

        &-title {
          position: absolute;
          top: 16px;
          left: 16px;
          font-weight: 600;
          line-height: 1.3;
        }

        &-desc {
          position: absolute;
          bottom: 40px;
          left: 16px;
          right: 16px;
          line-height: 1.5;
          opacity: 0.9;
          font-size: 14px;
        }

        &-btn {
          position: absolute;
          bottom: 16px;
          left: 16px;
          font-size: 14px;
          line-height: 1.4;
          color: $black;
        }
      }
    }
  }

  @include tablet-landscape {
    padding: 60px 0;

    &-desc {
      font-size: 18px;
    }

    &-wrapper {
      flex-direction: row;
      gap: 8px;
      height: 500px;
      align-items: stretch;
      margin-top: 40px;

      &-item {
        border-radius: 16px;
        height: 100%;
        flex: 1;
        transition: flex 1s cubic-bezier(0.4, 0, 0.2, 1);
        color: $white;

        &.is-active {
          flex: 2.5;
          color: $white;
        }

        .accordion-wrapper-item-title-vertical {
          position: absolute;
          bottom: 32px;
          right: 32px;
          margin: 0;
          z-index: 3;
          font-size: 20px;
          font-weight: 600;
          line-height: 1.3;
          writing-mode: vertical-rl;
          text-orientation: mixed;
          color: rgba(0, 0, 0, 0.5);
          writing-mode: sideways-lr;
          max-height: calc(100% - 64px);

          h1,
          h2,
          h3,
          h4,
          h5,
          h6 {
            margin: 0;
            font: inherit;
          }
        }

        &.is-active &-bg {
          &-before {
            opacity: 0;
            transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }

          &-after {
            opacity: 1;
          }
        }

        &-bg {
          &-before {
            display: block;
          }

          &-after {
            display: block;
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
            opacity: 0;
            transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);

            img {
              width: 100%;
              height: 100%;
              object-position: center center;
            }
          }
        }

        &-content {
          padding: 32px;
          display: flex;
          flex-direction: column;

          &-title {
            font-size: 20px;
            top: unset;
            left: unset;
            right: unset;
            position: relative;
            width: var(--accordion-item-width, 320px);
          }

          &-desc {
            top: unset;
            bottom: unset;
            left: unset;
            right: unset;
            font-size: 16px;
            position: relative;
            margin-top: 8px;
            width: var(--accordion-item-width, 320px);
          }

          .accordion-wrapper-item-content-btn {
            position: relative;
            top: unset;
            left: unset;
            margin-top: 24px;

            &-wrapper {
              display: inline-block;
            }
          }
        }
      }
    }
  }
}

.accordion-fade-enter-active {
  transition: opacity 1s ease;
}
.accordion-fade-leave-active {
  transition: opacity 1s ease;
}
.accordion-fade-enter-from,
.accordion-fade-leave-to {
  opacity: 0;
}
</style>
